import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Building2, Ship, AlertTriangle, ArrowRight, Loader2, LogOut, Anchor, Wallet, TrendingUp } from "lucide-react";

const money = (v) => (typeof v === "number" ? "$" + Math.round(v).toLocaleString() : "Not available");
const MODE_TONE = {
  "Active Season": "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
  "Pre-Season Ramp": "bg-sky-500/15 text-sky-300 border-sky-400/30",
  "Reduced Season": "bg-amber-500/15 text-amber-300 border-amber-400/30",
  "Off-Season": "bg-slate-500/15 text-slate-300 border-slate-400/30",
};

const Metric = ({ label, value, testid }) => (
  <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-3" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-widest text-slate-400 font-bold">{label}</div>
    <div className="text-xl font-bold text-white mt-1">{value}</div>
  </div>
);

export default function ExecutiveView() {
  const navigate = useNavigate();
  const { logout, loadExec } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState(null);

  useEffect(() => {
    api.get("/executive/group-summary").then(({ data }) => setData(data))
      .catch(() => setData(false)).finally(() => setLoading(false));
  }, []);

  const enterLocation = async (dealerId) => {
    setEntering(dealerId);
    try {
      await api.post("/executive/enter-location", { dealer_id: dealerId });
      await loadExec();
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open that location");
      setEntering(null);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-navy" data-testid="executive-loading">
      <Anchor className="w-10 h-10 text-gold animate-pulse" />
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-navy gap-3 text-center px-6" data-testid="executive-no-access">
      <Building2 className="w-10 h-10 text-gold" />
      <h1 className="text-xl font-bold text-white">No dealer group access</h1>
      <p className="text-slate-400 text-sm max-w-md">Your account does not have executive access to a dealer group. Contact your RecJet administrator.</p>
      <Button onClick={() => navigate("/dashboard")} className="bg-gold text-navy mt-2" data-testid="executive-go-dashboard">Go to dashboard</Button>
    </div>
  );

  const g = data.group || {};
  const s = data.snapshot || {};
  const rj = data.recjet_mind_summary || {};
  const readOnly = data.role === "view_only";

  return (
    <div className="min-h-screen bg-navy" data-testid="executive-view">
      {/* Header */}
      <header className="border-b-2 border-gold/50 glass-navy sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {g.logo ? (
              <div className="bg-white rounded-xl px-3 py-2 flex items-center shadow"><img src={resolveImg(g.logo)} alt={g.group_name} className="h-9 max-w-[160px] object-contain" /></div>
            ) : (
              <div className="w-12 h-12 rounded-xl gold-gradient flex items-center justify-center"><Building2 className="w-6 h-6 text-navy" /></div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight" data-testid="executive-group-name">{g.group_name || "Dealer Group"}</h1>
              <p className="text-sm text-slate-400">Marketing intelligence across your dealership group</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-xs uppercase tracking-widest text-gold font-bold">{data.location_count} Locations</div>
              <div className="text-xs text-slate-400" data-testid="executive-month">{data.reporting_month}</div>
            </div>
            <Button variant="outline" onClick={logout} className="border-white/20 text-slate-200 hover:text-white" data-testid="executive-logout"><LogOut className="w-4 h-4 mr-1.5" /> Log out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8 space-y-8">
        {readOnly && (
          <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-400/30 rounded-lg px-3 py-2 inline-block" data-testid="executive-readonly-banner">View only access. You can inspect locations but cannot make changes.</div>
        )}

        {/* Snapshot */}
        <section>
          <h2 className="text-sm font-bold uppercase tracking-widest text-gold mb-3">Group Snapshot</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="executive-snapshot">
            <Metric label="Locations" value={data.location_count} testid="snap-locations" />
            <Metric label="Active Inventory" value={s.active_inventory} testid="snap-inventory" />
            <Metric label="Marketing Opportunities" value={s.opportunities} testid="snap-opportunities" />
            <Metric label="Planned Paid Media" value={money(s.planned_media)} testid="snap-planned" />
            <Metric label="Meta Allocation" value={money(s.meta)} testid="snap-meta" />
            <Metric label="Google Allocation" value={money(s.google)} testid="snap-google" />
            <Metric label="Committed Paid Spend" value={money(s.committed)} testid="snap-committed" />
            <Metric label="Remaining Available" value={money(s.available)} testid="snap-available" />
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Protected reserve across the group: {money(s.reserve)}. Each location budget is independent. Group totals are for reporting only.</p>
        </section>

        {/* RecJet Mind + Needs attention */}
        <section className="grid lg:grid-cols-2 gap-5">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="executive-recjet-summary">
            <div className="flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4 text-gold" /><h2 className="text-sm font-bold uppercase tracking-widest text-gold">RecJet Mind Across Your Group</h2></div>
            <ul className="space-y-2 text-sm text-slate-200">
              <li>{rj.locations_evaluated} locations evaluated</li>
              <li>{rj.locations_with_paid_opportunities} locations currently have paid opportunities</li>
              <li>{rj.locations_reduced_or_off_season} locations operating in Reduced or Off-Season</li>
              <li>{rj.inventory_opportunities_total} inventory opportunities currently need attention</li>
            </ul>
          </div>
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="executive-needs-attention">
            <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-4 h-4 text-amber-400" /><h2 className="text-sm font-bold uppercase tracking-widest text-amber-300">Needs Attention</h2></div>
            {(data.needs_attention || []).length === 0 ? (
              <p className="text-sm text-slate-400">Nothing needs attention across your group right now.</p>
            ) : (
              <ul className="space-y-2">
                {data.needs_attention.map((a, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-slate-200"><span className="font-semibold text-white">{a.name}:</span> {a.reason}</span>
                    <button onClick={() => enterLocation(a.dealer_id)} className="text-gold hover:underline text-xs font-semibold shrink-0" data-testid={`attention-go-${a.dealer_id}`}>Open</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        {/* Location cards */}
        <section>
          <h2 className="text-sm font-bold uppercase tracking-widest text-gold mb-3">Locations</h2>
          <div className="grid md:grid-cols-2 gap-5" data-testid="executive-locations">
            {data.locations.map((l) => {
              const rm = l.recjet_mind || {};
              const b = rm.boat || {};
              return (
                <div key={l.dealer_id} className="rounded-2xl bg-white/5 border border-white/10 p-5 hover:border-gold/40 transition-colors" data-testid={`location-card-${l.dealer_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      {l.logo ? (
                        <div className="bg-white rounded-lg px-2 py-1 flex items-center"><img src={resolveImg(l.logo)} alt={l.name} className="h-8 max-w-[120px] object-contain" /></div>
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gold/15 flex items-center justify-center"><Building2 className="w-5 h-5 text-gold" /></div>
                      )}
                      <div>
                        <div className="font-bold text-white">{l.name}</div>
                        <div className="text-xs text-slate-400">{[l.city, l.state].filter(Boolean).join(", ") || l.location_label}</div>
                      </div>
                    </div>
                    {l.operating_mode && <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${MODE_TONE[l.operating_mode] || "bg-white/10 text-slate-300 border-white/20"}`}>{l.operating_mode}</span>}
                  </div>

                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div><div className="text-lg font-bold text-white">{l.active_inventory}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">Inventory</div></div>
                    <div><div className="text-lg font-bold text-white">{l.opportunities}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">Opportunities</div></div>
                    <div><div className="text-lg font-bold text-white">{money(l.remaining_available_budget)}</div><div className="text-[10px] uppercase tracking-wider text-slate-400">Avail. Budget</div></div>
                  </div>

                  <div className="mt-4 rounded-xl bg-black/20 border border-white/10 p-3 flex items-center gap-3" data-testid={`location-recjet-${l.dealer_id}`}>
                    {b.thumbnail ? (
                      <img src={resolveImg(b.thumbnail)} alt="" className="w-16 h-12 object-cover rounded-md" />
                    ) : (
                      <div className="w-16 h-12 rounded-md bg-white/5 flex items-center justify-center"><Ship className="w-5 h-5 text-slate-500" /></div>
                    )}
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-gold font-bold">RecJet Mind recommends</div>
                      {b.model ? (
                        <>
                          <div className="text-sm font-semibold text-white truncate">{[b.year, b.manufacturer, b.model].filter(Boolean).join(" ")}</div>
                          <div className="text-xs text-slate-400 truncate">{rm.best_fit_buyer ? `Target: ${rm.best_fit_buyer}` : ""}{rm.dealer_spend ? ` • $${Math.round(rm.dealer_spend)} paid` : (rm.organic_paid_strategy === "organic" ? " • Organic" : "")}</div>
                        </>
                      ) : (
                        <div className="text-sm text-slate-400">No active recommendation</div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Wallet className="w-3.5 h-3.5" /> Meta {money(l.meta_budget)}</span>
                    <span>Google {money(l.google_budget)}</span>
                    <span>Reserve {money(l.reserve)}</span>
                  </div>

                  <Button onClick={() => enterLocation(l.dealer_id)} disabled={entering === l.dealer_id} className="w-full mt-4 bg-gold hover:bg-gold/90 text-navy font-semibold" data-testid={`view-location-${l.dealer_id}`}>
                    {entering === l.dealer_id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} View Location <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Comparison */}
        <section>
          <h2 className="text-sm font-bold uppercase tracking-widest text-gold mb-3">Compare Locations</h2>
          <div className="rounded-2xl bg-white/5 border border-white/10 overflow-x-auto" data-testid="executive-comparison">
            <table className="w-full text-sm">
              <thead className="text-slate-400">
                <tr className="text-left border-b border-white/10">
                  {["Location", "Active Inventory", "Opportunities", "Planned Media", "Remaining Budget", "Operating Mode"].map((h) => <th key={h} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {data.locations.map((l) => (
                  <tr key={l.dealer_id} className="text-slate-200" data-testid={`compare-row-${l.dealer_id}`}>
                    <td className="px-4 py-3 font-medium text-white whitespace-nowrap">{l.name}</td>
                    <td className="px-4 py-3">{l.active_inventory}</td>
                    <td className="px-4 py-3">{l.opportunities}</td>
                    <td className="px-4 py-3">{money(l.planned_media)}</td>
                    <td className="px-4 py-3">{money(l.remaining_available_budget)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{l.operating_mode || "Not set"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
